import React, { useState, useEffect } from 'react';
import { Download, X, Share, Smartphone } from 'lucide-react';
import { subscribeInstallPrompt, triggerInstall, isStandalone } from '../utils/pwaInstall';

export const PWAInstallPrompt = () => {
  const [hasPrompt, setHasPrompt] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showAndroidManualHelp, setShowAndroidManualHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) {
      setIsInstalled(true);
      return;
    }

    const ua = navigator.userAgent;
    let isiOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    // Modern iPads (iOS 13+) lie about being Mac. Touch points > 1 + Mac UA = iPad in disguise.
    if (!isiOS && /Macintosh/.test(ua) && navigator.maxTouchPoints > 1) {
      isiOS = true;
    }
    setIsIOS(isiOS);

    // Check if dismissed recently (24 hours)
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    const wasDismissed = dismissed && Date.now() - parseInt(dismissed) < 24 * 60 * 60 * 1000;

    if (isiOS) {
      if (!wasDismissed) setShowBanner(true);
      return;
    }

    // Android: subscribe to the globally captured beforeinstallprompt.
    // If we haven't received it yet at mount time (or already consumed it),
    // we still show the banner so the user has a path — tapping Install will
    // either fire the native dialog (if available) or open the manual help
    // overlay with Chrome menu instructions.
    const unsub = subscribeInstallPrompt((available) => {
      setHasPrompt(available);
      if (!wasDismissed) setShowBanner(true);
    });
    return unsub;
  }, []);

  const handleInstall = async () => {
    const { available, outcome } = await triggerInstall();
    if (available && outcome === 'accepted') {
      setShowBanner(false);
      setIsInstalled(true);
      return;
    }
    if (!available) {
      // Chrome never fired (or already consumed) — fall back to manual instructions.
      setShowAndroidManualHelp(true);
    }
  };

  const dismiss = () => {
    setShowBanner(false);
    setShowAndroidManualHelp(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  // Toggle a body class so the mobile top-bar can shift down while this banner is showing.
  // Prevents the banner (z-[70]) from covering the notification bell (z-50) at top:0.
  useEffect(() => {
    const visible = !isInstalled && (showBanner || showAndroidManualHelp);
    if (visible) {
      document.body.classList.add('pwa-banner-visible');
    } else {
      document.body.classList.remove('pwa-banner-visible');
    }
    return () => document.body.classList.remove('pwa-banner-visible');
  }, [isInstalled, showBanner, showAndroidManualHelp]);

  if (isInstalled) return null;
  if (!showBanner && !showAndroidManualHelp) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[70] md:hidden" data-testid="pwa-install-banner">
      <div className="bg-bundle-green text-white px-4 py-3 shadow-lg">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 bg-white rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm">
            <img src="/logo.png" alt="Bundle Golf" className="w-8 h-8 object-contain" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-body text-sm font-bold">Get the Bundle Golf App</p>
            <p className="font-body text-[11px] text-white/80">
              {isIOS
                ? 'Install for push notifications & full experience'
                : 'Install to your home screen — free & instant'}
            </p>
          </div>
          {!isIOS && (
            <button
              data-testid="pwa-install-button"
              onClick={handleInstall}
              className="px-4 py-2 bg-white text-bundle-green font-body text-xs font-bold rounded-full flex-shrink-0 shadow-sm"
            >
              {hasPrompt ? 'Install' : 'How to'}
            </button>
          )}
          <button onClick={dismiss} className="p-1 flex-shrink-0 -mr-1" data-testid="pwa-install-dismiss">
            <X className="w-4 h-4 text-white/50" />
          </button>
        </div>

        {/* iOS-specific step-by-step instructions */}
        {isIOS && (
          <div className="mt-3 bg-white/10 rounded-xl px-3 py-2.5 backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-white/90">
                <span className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0">1</span>
                <span className="text-xs font-body">Tap</span>
                <span className="inline-flex items-center justify-center w-6 h-6 bg-white/20 rounded">
                  <Share className="w-3.5 h-3.5" />
                </span>
              </div>
              <div className="flex items-center gap-2 text-white/90">
                <span className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0">2</span>
                <span className="text-xs font-body">&quot;Add to Home Screen&quot;</span>
                <Smartphone className="w-3.5 h-3.5 text-white/70 flex-shrink-0" />
              </div>
            </div>
          </div>
        )}

        {/* Android manual fallback — shown when Chrome refused to fire beforeinstallprompt */}
        {!isIOS && showAndroidManualHelp && (
          <div className="mt-3 bg-white/10 rounded-xl px-3 py-3 backdrop-blur-sm" data-testid="pwa-android-manual-help">
            <p className="text-[11px] text-white/80 mb-2">Chrome didn&apos;t show the install dialog. Add it manually in 2 steps:</p>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-white/90 text-xs">
                <span className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0">1</span>
                <span>Tap the Chrome menu <span className="font-bold tracking-widest">⋮</span> (top-right)</span>
              </div>
              <div className="flex items-center gap-2 text-white/90 text-xs">
                <span className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0">2</span>
                <span>Tap <strong>&quot;Install app&quot;</strong> or <strong>&quot;Add to Home screen&quot;</strong></span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Hook for other components to trigger install
export const usePWAInstall = () => ({
  isInstalled: isStandalone(),
  isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream,
  triggerInstall,
});
